import { operators as o, common as c } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { applyCustomSegment } from './common.util';

const RETENTION_OFFSET = 30;

function getEventSource (targetType: c.TargetType, targetId: c.TrackTypeId, timeSeries: c.TimeSeries) {
    switch (targetType) {
        case 'page':
            return o.sources.pageEvents({ pageId: targetId, timeSeries });
        case 'feature':
            return o.sources.featureEvents({ featureId: targetId, timeSeries });
        case 'trackType':
            return o.sources.trackEvents({ trackTypeId: targetId, timeSeries });
        default:
            throw new Error(`Unsupported targetType: ${targetType}`);
    }
}

function createOnboardingSegment (
    targetType: c.TargetType,
    targetId: c.TrackTypeId,
    startDate: string,
    endDate: string,
    metadataAppPath: string,
    eventCountCondition: string,
    usesAccounts: boolean,
    segmentId?: string,
) {
    const visitorsSegment = applyCustomSegment(
        o.pipeline(
            o.spawn(
                o.pipeline(o.sources.visitors(), o.cat()),
                o.pipeline(
                    getEventSource(targetType, targetId, {
                        period: 'dayRange',
                        first: `${startDate}`,
                        last: `${endDate}`,
                    }),
                    o.group(['visitorId'], {
                        eventCount: { sum: 'numEvents' },
                    }),
                    o.select({
                        visitorId: 'visitorId',
                        eventCount: 'eventCount',
                    }),
                ),
            ),
            o.join('visitorId', 2),
            o.evalExpression({
                eventCount: 'if(isNil(eventCount),0,eventCount)',
            }),
            o.filter(
                `!isNil(metadata.auto${metadataAppPath}.lastvisit)&&metadata.auto${metadataAppPath}.lastvisit>=${startDate}`,
            ),
            o.filter(`eventCount${eventCountCondition}`),
            o.filter(
                `metadata.auto${metadataAppPath}.firstvisit<=${endDate}&&metadata.auto${metadataAppPath}.firstvisit>=${startDate}`,
            ),
            o.select({
                visitorId: 'visitorId',
            }),
        ),
        segmentId,
    );
    const accountsSegment = applyCustomSegment(
        o.pipeline(
            o.sources.visitors(),
            o.evalExpression({
                accountId: 'metadata.auto.accountids',
            }),
            o.unwind('accountId', { keepEmpty: true }),
            o.bulkExpand('account', 'account', 'accountId'),
            o.merge(
                ['accountId'],
                o.mappings({
                    eventCount: 'eventCount',
                }),
                o.pipeline(
                    getEventSource(targetType, targetId, {
                        period: 'dayRange',
                        first: `${startDate}`,
                        last: `${endDate}`,
                    }),
                    o.group(['accountId'], {
                        eventCount: { sum: 'numEvents' },
                    }),
                    o.filter('accountId != ""'),
                    o.select({
                        accountId: 'accountId',
                        eventCount: 'eventCount',
                    }),
                ),
            ),
            o.evalExpression({
                eventCount: 'if(isNil(eventCount),0,eventCount)',
            }),
            o.filter(
                `!isNil(metadata.auto${metadataAppPath}.lastvisit)&&metadata.auto${metadataAppPath}.lastvisit>=${startDate}`,
            ),
            o.filter(`eventCount${eventCountCondition}`),
            o.filter(
                `account.auto${metadataAppPath}.firstvisit<=${endDate}&&account.auto${metadataAppPath}.firstvisit>=${startDate}`,
            ),
            o.select({
                visitorId: 'visitorId',
                accountId: 'accountId',
            }),
        ),
        segmentId,
    );

    return usesAccounts ? accountsSegment : visitorsSegment;
}

function getIdType (usesAccounts: boolean) {
    return usesAccounts ? 'accountId' : 'visitorId';
}

function getRetentionCountType (usesAccounts: boolean) {
    return usesAccounts ? 'accounts' : 'visitors';
}

export default {
    name: 'OnboardingRetentionAggregation',
    productArea: PRODUCT_AREAS.IMPACT_BRIEF,
    subProductArea: SUB_PRODUCT_AREAS.ONBOARDING,
    responseFormat: 'row',
    build: ({
        appId,
        targetType,
        targetId,
        startDate,
        endDate,
        usesAccounts,
        segmentId,
    }: {
        appId: c.AppIdType;
        targetType: c.TargetType;
        targetId: c.TrackTypeId;
        startDate: string;
        endDate: string;
        usesAccounts: boolean;
        segmentId?: string;
    }) => {
        if (!appId) {
            throw new Error('appId is required for OnboardingRetentionAggregation');
        }
        if (!targetId) {
            throw new Error('targetId is required for OnboardingRetentionAggregation');
        }
        const metadataAppPath = `_${appId.toString().replace('-', '_')}`;

        return o.pipeline(
            o.spawn(
                o.pipeline(
                    o.sources.timespan({
                        appId,
                    }),
                    o.identified('visitorId'),
                    createOnboardingSegment(
                        targetType,
                        targetId,
                        startDate,
                        endDate,
                        metadataAppPath,
                        '>=1',
                        usesAccounts,
                        segmentId,
                    ),
                    o.group(
                        [getIdType(usesAccounts)],
                        o.groupField('activeDays', { daysActiveUnion: 'activeDays' }),
                        o.groupField('firstTime', { min: 'firstTime' }),
                        o.groupField('lastTime', { max: 'lastTime' }),
                        o.groupField('visitorId', { first: 'visitorId' }),
                        o.groupField('accountId', { first: 'accountId' }),
                        o.groupField('parentAccountId', { first: 'parentAccountId' }),
                    ),
                    o.retention({
                        timeSeries: {
                            period: 'monthRange',
                            first: startDate,
                            count: 7,
                        },
                        offset: RETENTION_OFFSET,
                        cohortField: 'beginningOfCohort',
                        firstvisitField: 'firstvisit',
                        relativePeriodsField: 'relativePeriods',
                        cohortMode: 'first',
                    }),
                    o.unwind('retention'),
                    o.group(
                        ['retention.label'],
                        o.groupField(getRetentionCountType(usesAccounts), { count: getIdType(usesAccounts) }),
                        o.groupField('relativePeriods', { first: 'retention.relativePeriods' }),
                    ),
                    o.fork(
                        o.pipeline(
                            o.filter('relativePeriods == 0'),
                            o.group([], {
                                onboardedTotalNum: { sum: getRetentionCountType(usesAccounts) },
                            }),
                        ),
                        o.pipeline(
                            o.filter('relativePeriods == 2'),
                            o.group([], {
                                onboardedNumRetained: { sum: getRetentionCountType(usesAccounts) },
                            }),
                        ),
                    ),
                    o.join(),
                    o.evalExpression({
                        percentageOnboardedRetained:
                            'if(onboardedTotalNum == 0, 0, (onboardedNumRetained / onboardedTotalNum) * 100)',
                    }),
                ),
                o.pipeline(
                    o.sources.timespan({
                        appId,
                    }),
                    o.identified('visitorId'),
                    createOnboardingSegment(
                        targetType,
                        targetId,
                        startDate,
                        endDate,
                        metadataAppPath,
                        '==0',
                        usesAccounts,
                        segmentId,
                    ),
                    o.group(
                        [getIdType(usesAccounts)],
                        o.groupField('activeDays', { daysActiveUnion: 'activeDays' }),
                        o.groupField('firstTime', { min: 'firstTime' }),
                        o.groupField('lastTime', { max: 'lastTime' }),
                        o.groupField('visitorId', { first: 'visitorId' }),
                        o.groupField('accountId', { first: 'accountId' }),
                        o.groupField('parentAccountId', { first: 'parentAccountId' }),
                    ),
                    o.retention({
                        timeSeries: {
                            period: 'monthRange',
                            first: startDate,
                            count: 7,
                        },
                        offset: RETENTION_OFFSET,
                        cohortField: 'beginningOfCohort',
                        firstvisitField: 'firstvisit',
                        relativePeriodsField: 'relativePeriods',
                        cohortMode: 'first',
                    }),
                    o.unwind('retention'),
                    o.group(
                        ['retention.label'],
                        o.groupField(getRetentionCountType(usesAccounts), { count: getIdType(usesAccounts) }),
                        o.groupField('relativePeriods', { first: 'retention.relativePeriods' }),
                    ),
                    o.fork(
                        o.pipeline(
                            o.filter('relativePeriods == 0'),
                            o.group([], {
                                notOnboardedTotalNum: { sum: getRetentionCountType(usesAccounts) },
                            }),
                        ),
                        o.pipeline(
                            o.filter('relativePeriods == 2'),
                            o.group([], {
                                notOnboardedNumRetained: { sum: getRetentionCountType(usesAccounts) },
                            }),
                        ),
                    ),
                    o.join(),
                    o.evalExpression({
                        percentageNotOnboardedRetained:
                            'if(notOnboardedTotalNum == 0, 0, (notOnboardedNumRetained / notOnboardedTotalNum) * 100)',
                    }),
                ),
            ),
            o.join(),
            o.select({
                notOnboardedTotalNum: 'notOnboardedTotalNum',
                notOnboardedNumRetained: 'notOnboardedNumRetained',
                percentageNotOnboardedRetained: 'percentageNotOnboardedRetained',
                onboardedTotalNum: 'onboardedTotalNum',
                onboardedNumRetained: 'onboardedNumRetained',
                percentageOnboardedRetained: 'percentageOnboardedRetained',
            }),
            o.evalExpression({
                id: `"onboardingRetentionAggregation_${targetId}"`,
            }),
        );
    },
};
